import AsyncStorage from "@react-native-async-storage/async-storage";
import { combineReducers, configureStore, Middleware } from "@reduxjs/toolkit";
import { Platform } from "react-native";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  type PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import createWebStorage from "redux-persist/es/storage/createWebStorage";
import { persistEncryptionTransform } from "../utils/encryption";
import { resetAll } from "./actions/resetAll";
import apiSlice from "./slices";
import { authReducer } from "./slices/auth";
import brokerInfoReducer from "./slices/brokerInfoSlice";
import { investmentFormReducer } from "./slices/investmentFormSlice";
import { recentlyViewedReducer } from "./slices/recentlyViewed";
import { userReducer } from "./slices/user";

const createNoopStorage = () => ({
  getItem(_key: string) {
    return Promise.resolve(null);
  },
  setItem(_key: string, value: string) {
    return Promise.resolve(value);
  },
  removeItem(_key: string) {
    return Promise.resolve();
  },
});

const SelectedStorage =
  Platform.OS === "web"
    ? typeof window !== "undefined"
      ? createWebStorage("session")
      : createNoopStorage()
    : AsyncStorage;

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: "root",
  storage: SelectedStorage,
  whitelist:
    Platform.OS === "web"
      ? ["user", "recentlyViewed", "investmentForm"] // persist investmentForm on web
      : ["user", "recentlyViewed"], // only user and recentlyViewed on mobile
  blacklist: ["auth"],
  transforms: [persistEncryptionTransform],
};

const rootReducer = combineReducers({
  user: userReducer,
  auth: authReducer,
  investmentForm: investmentFormReducer,
  brokerInfo: brokerInfoReducer,
  recentlyViewed: recentlyViewedReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
});

const rootReducerWithReset: typeof rootReducer = (state, action) => {
  if (resetAll.match(action)) {
    return rootReducer(undefined, action); // forces all slices to their initialState
  }
  return rootReducer(state, action);
};

// Then pass it to persistReducer instead of rootReducer ↓
const persisted = persistReducer(persistConfig, rootReducerWithReset);

// Middleware to handle resetAll action and reset API cache
const resetMiddleware: Middleware = (store) => (next) => async (action) => {
  if (resetAll.match(action)) {
    store.dispatch(apiSlice.util.resetApiState());
    await persistor.purge();
  }
  return next(action);
};

const store = configureStore({
  reducer: persisted,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        // sipStartDate and sipEndDate are intentionally stored as Dates across
        // the app (the SIP duration feature derives sipEndDate as a Date too).
        ignoredActionPaths: ["payload.sipStartDate", "payload.sipEndDate"],
        ignoredPaths: [
          "investmentForm.investmentDetails.sipStartDate",
          "investmentForm.investmentDetails.sipEndDate",
        ],
      },
    }).concat(apiSlice.middleware, resetMiddleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
